import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { ModuleRegistry } from '../modules/registry';
import { useFollowUpCount } from '../hooks/quotes/useFollowUpCount';
import { useQuoteMentionCount } from '../hooks/quotes/useQuoteMentionBadge';
import { useQuoteWorksheetSwitch } from '../hooks/quoting/useQuoteWorksheetSwitch';
import { worksheetNavItems } from '../modules/quoting/nav';
import { useAgentTaskCount } from '../hooks/agents/useAgentTasks';
import { useUnlinkedInvoiceCount } from '../hooks/projects/useUnlinkedInvoiceCount';
import { useContentReviewCount, useNewMediaInquiryCount } from '../hooks/marketing/useMarketingNavBadges';
import { setNavState, clearNavState } from '../lib/contextCapture';
import { blockedAgentConfigIds } from '../lib/agentRunAccess';
import { withAgentTaskBadge, withBadgeAt } from '../lib/agents/agentTaskNavBadge';
import type { UserRole } from '../types/firestore';
import type { PageAccess } from '../modules/pageAccess';

// Organization owner emails who have implicit access to all modules
const OWNER_EMAILS = ['nick@onpointgen.com', 'dillan@onpointgen.com'];

/**
 * Module definitions for the Quote-to-Cash platform (EnterpriseHub)
 *
 * 8-Phase Architecture:
 * P1: CRM - Full sales funnel from cold research → closed deal
 * P2: Quoting - Quote creation, pricing, PDF, approvals (current core)
 * P3: Contracts - Contract review, onboarding, execution management
 * P4: Billing & Collections - Invoice coordination, AR, collections
 * P5: Customer Support - Zendesk-inspired ticketing, call logging, team collaboration
 * P6: Data Explorer - Data connections, sync, browse & search across sources
 * P7: Assets - Universal asset management, inspections, compliance
 * P8: Field Service Forms - Form builder, mobile forms, service reports
 *
 * Note: Prospecting was merged into CRM for a single continuous pipeline.
 * Note: P6 Explorer is powered by DataHub (migrated from qbo-hub).
 * Note: P7 & P8 migrated from FieldHub.
 */
export type ModuleId = 'P1' | 'P2' | 'P3' | 'P4' | 'P5' | 'P6' | 'P7' | 'P8' | 'P9' | 'P10' | 'P11' | 'P12' | 'P13' | 'P14' | 'P15' | 'P16' | 'P17' | 'P18' | 'P19' | 'P20' | 'AI_SYSTEMS' | 'TRAINING' | 'RUDY' | 'PULSE' | 'SHOP';

export interface Module {
  id: ModuleId;
  name: string;
  shortName: string;
  urlPrefix: string;
  description: string;
  icon: string;
  status: 'live' | 'in_development' | 'planned';
  /** External URL — if set, clicking this module opens the URL in a new tab instead of navigating internally */
  externalUrl?: string;
}

export const MODULES: Record<ModuleId, Module> = {
  P1: {
    id: 'P1',
    name: 'Sales',
    shortName: 'Sales',
    urlPrefix: '/crm',
    description: 'Full sales funnel: research, outreach, pipeline, deals',
    icon: '🎯',
    status: 'live',
  },
  P2: {
    id: 'P2',
    name: 'Quoting',
    shortName: 'Quoting',
    urlPrefix: '/quoting',
    description: 'Quote creation, pricing engine, PDF generation',
    icon: '🏷️',
    status: 'live',
  },
  P3: {
    id: 'P3',
    name: 'Contracts',
    shortName: 'Contracts',
    urlPrefix: '/field-service/contracts',
    description: 'Merged into Field Service (P11)',
    icon: '📋',
    status: 'planned',
  },
  P4: {
    id: 'P4',
    name: 'Finance',
    shortName: 'Finance',
    urlPrefix: '/billing',
    description: 'Invoice coordination, AR aging, collections',
    icon: '💵',
    status: 'live',
  },
  P5: {
    id: 'P5',
    name: 'Support',
    shortName: 'Support',
    urlPrefix: '/support/inbox/cases',
    description: 'Support cases, call logging, team collaboration',
    icon: '🎧',
    status: 'live',
  },
  P6: {
    id: 'P6',
    name: 'Central Command',
    shortName: 'Command',
    urlPrefix: '/command',
    description: 'Executive dashboards, operations intelligence, and data health monitoring',
    icon: '🛰️',
    status: 'live',
  },
  P7: {
    id: 'P7',
    name: 'Assets',
    shortName: 'Assets',
    urlPrefix: '/assets',
    description: 'Universal asset management — inventory, compliance, inspections, and task tracking',
    icon: '🏗️',
    status: 'live',
  },
  P8: {
    id: 'P8',
    name: 'Forms',
    shortName: 'Forms',
    urlPrefix: '/forms',
    description: 'Custom form builder, mobile form filling, and service report generation',
    icon: '📋',
    status: 'live',
  },
  P9: {
    id: 'P9',
    name: 'HR',
    shortName: 'HR',
    urlPrefix: '/hr',
    description: 'Employee lifecycle management, onboarding, payroll, and compliance',
    icon: '🪪',
    status: 'live',
  },
  P10: {
    id: 'P10',
    name: 'Projects',
    shortName: 'Projects',
    urlPrefix: '/projects',
    description: 'Installation project management, equipment tracking, scheduling, and document management',
    icon: '🗂️',
    status: 'live',
  },
  P11: {
    id: 'P11',
    name: 'Field Service',
    shortName: 'Field Service',
    urlPrefix: '/field-service',
    description: 'Service tickets, contracts, scheduling, generators, and field service reports',
    icon: '🔧',
    status: 'live',
  },
  P12: {
    id: 'P12',
    name: 'Technician',
    shortName: 'Technician',
    urlPrefix: '/technician',
    description: 'Pre-service briefings, AI job assistant, and senior technician curation',
    icon: '👷',
    status: 'live',
  },
  P13: {
    id: 'P13',
    name: 'Time Tracking',
    shortName: 'Time',
    urlPrefix: '/time',
    description: 'Time tracking, GPS capture, timesheet approval, and payroll export',
    icon: '⏱',
    status: 'live',
  },
  P14: {
    id: 'P14',
    name: 'Recruiting',
    shortName: 'Recruiting',
    urlPrefix: '/recruiting',
    description: 'AI-powered talent acquisition and recruiting pipeline',
    icon: '🔍',
    status: 'planned',
  },
  P15: {
    id: 'P15',
    name: 'Marketing',
    shortName: 'Marketing',
    urlPrefix: '/marketing',
    description: 'Digital marketing command center — content calendar, AI drafting, approval workflows, and multi-channel publishing',
    icon: '📣',
    status: 'live',
  },
  P16: {
    id: 'P16',
    name: 'Huddle Hub',
    shortName: 'Huddle',
    urlPrefix: '/huddle',
    description: 'Operating rhythm — recurring meetings, action items, decisions, announcements, vision/strategy, accountability chart. Replaces Moose.',
    icon: '🤝',
    status: 'live',
  },
  P17: {
    id: 'P17',
    name: 'Safety',
    shortName: 'Safety',
    urlPrefix: '/safety',
    description: 'Workplace safety management, incident tracking, OSHA compliance, and insurance',
    icon: 'Shield',
    status: 'live',
  },
  P18: {
    id: 'P18',
    name: 'Investor Relations',
    shortName: 'IR',
    urlPrefix: '/investors',
    description: 'Investor relations, reporting, and stakeholder communications',
    icon: 'Landmark',
    status: 'live',
  },
  P19: {
    id: 'P19',
    name: 'Workshop',
    shortName: 'Workshop',
    urlPrefix: '/workshop',
    description: 'Per-user productivity utilities — letters, investor updates, briefs, meeting prep, e-sign, drafting, research, narratives, workflows',
    icon: 'Wrench',
    status: 'planned',
  },
  P20: {
    id: 'P20',
    name: 'Insurance',
    shortName: 'Insurance',
    urlPrefix: '/insurance',
    description: 'Policy management, vendor COIs, customer certificates, and claims',
    icon: 'FileShield',
    status: 'live',
  },
  AI_SYSTEMS: {
    id: 'AI_SYSTEMS',
    name: 'AI',
    shortName: 'AI',
    urlPrefix: '/ai-systems',
    description: 'AI Systems - prompts, learning, discovery, and deployments',
    icon: '🧠',
    status: 'live',
  },
  TRAINING: {
    id: 'TRAINING',
    name: 'Training',
    shortName: 'Training',
    urlPrefix: '/training',
    externalUrl: 'https://training.onpointgen.com',
    description: 'Team training and learning resources',
    icon: '🎓',
    status: 'live',
  },
  RUDY: {
    id: 'RUDY',
    name: 'Rudy',
    shortName: 'Rudy',
    urlPrefix: '/rudy',
    externalUrl: 'https://askrudy.io',
    description: 'Your expert senior tech on call 24/7',
    icon: '💬',
    status: 'live',
  },
  PULSE: {
    id: 'PULSE',
    name: 'OnPoint Pulse',
    shortName: 'Pulse',
    urlPrefix: '/pulse',
    externalUrl: 'https://pulse.onpointgen.com',
    description: 'AI-attended generator monitoring — live telemetry, alert triage, and dispatch coordination',
    icon: '📡',
    status: 'live',
  },
  SHOP: {
    id: 'SHOP',
    name: 'Shop',
    shortName: 'Shop',
    urlPrefix: '/shop',
    description: 'In-shop service and repair jobs: intake, evidence, approvals, work log and billing handoff',
    icon: 'Warehouse',
    status: 'in_development',
  },
};

// Navigation items for each module
export interface NavItem {
  label: string;
  path: string;
  icon?: string;
  /** Dynamic badge count (e.g., pending follow-ups) */
  badge?: number;
  /** Child items create a dropdown menu instead of a direct link */
  children?: NavItem[];
  /** When true, only visible to Manager and Admin roles */
  requireManagerOrAdmin?: boolean;
  /** When true, only visible to Admin and Owner roles */
  requireAdmin?: boolean;
  /** Only visible to these roles — mirrors the route's own requiredRole */
  requiredRoles?: UserRole[];
  /** Only visible to a user whose role this predicate accepts — mirrors the route's allowRole */
  allowRole?: (role: unknown) => boolean;
  /** Hidden from an External account (users.isExternal) — see modules/types.ts */
  hideForExternal?: boolean;
  /** Visible only to a viewer who may open a page with this access — see modules/types.ts */
  requirePage?: PageAccess;
  /** Render as a call-to-action button rather than a plain link — see modules/types.ts */
  cta?: boolean;
}

export const MODULE_NAV_ITEMS: Record<ModuleId, NavItem[]> = {
  P1: [
    // CRM: Full sales funnel from prospecting through closed deals
    { label: 'Leads', path: '/crm/leads' },
  ],
  P2: [
    // Quoting: Quote creation, pricing, approvals
    { label: 'Dashboard', path: '/quoting' },
    { label: 'Quotes', path: '/quoting/quotes' },
    { label: 'Customers', path: '/quoting/customers' },
    { label: 'Equipment', path: '/quoting/equipment' },
    { label: 'Price Book', path: '/quoting/price-book' },
  ],
  P3: [
    // Contracts: Merged into Field Service (P11)
    { label: 'Dashboard', path: '/field-service/contracts' },
  ],
  P4: [
    // Finance: the registry menu (modules/billing/nav.ts) wins; this fallback mirrors its top level.
    { label: 'Dashboard', path: '/billing/dashboard' },
    { label: 'Invoice', path: '/billing/invoicing/service' },
    { label: 'Collect', path: '/billing/collections' },
    { label: 'Tools', path: '/billing/invoicing/link-review' },
    { label: 'Settings', path: '/billing/settings' },
  ],
  P5: [
    // Customer Support: Dashboard, cases, call logging
    { label: 'Dashboard', path: '/support/dashboard' },
    { label: 'Cases', path: '/support/inbox/cases' },
    { label: 'Call Logs', path: '/support/inbox/calls' },
  ],
  P6: [
    // Central Command: Mission control — strategic → operational → infrastructure
    { label: 'Executive', path: '/command' },
    { label: 'Operations', path: '/command/operations' },
    { label: 'Sales', path: '/command/sales' },
    { label: 'Fleet & Assets', path: '/command/fleet' },
    { label: 'Data Health', path: '/command/health' },
    { label: 'Data Browser', path: '/command/data' },
  ],
  P7: [
    // Fleet Management: Simplified use-case driven navigation
    // Inspections and Mileage accessed via Vehicle detail tabs
    // Reports accessed via Dashboard quick links
    { label: 'Dashboard', path: '/assets' },
    { label: 'Inventory', path: '/assets/inventory' },
    { label: 'Drivers', path: '/assets/drivers' },
    { label: 'Inspections', path: '/assets/inspections' },
    { label: 'Tasks', path: '/assets/tasks' },
    { label: 'Compliance', path: '/assets/compliance' },
    { label: 'Templates', path: '/assets/templates' },
  ],
  P8: [
    // Field Service Forms: Form builder, submissions, review
    { label: 'Dashboard', path: '/forms' },
    { label: 'Fill Form', path: '/forms/fill' },
    { label: 'Templates', path: '/forms/templates' },
    { label: 'Submissions', path: '/forms/submissions' },
  ],
  P9: [
    // HR: Jobs-to-be-done navigation
    { label: 'Dashboard', path: '/hr', icon: 'LayoutDashboard' },
    { label: 'Employees', path: '/hr/employees', icon: 'Users' },
    { label: 'Onboarding Templates', path: '/hr/templates', icon: 'UserPlus' },
    { label: 'Compliance', path: '/hr/policies', icon: 'ShieldCheck' },
    { label: 'Audit Log', path: '/hr/audit', icon: 'FileSearch' },
  ],
  P10: [
    // Project Management: Installation projects, equipment, scheduling
    { label: 'Dashboard', path: '/projects' },
    { label: 'All Projects', path: '/projects/board' },
    { label: 'Equipment', path: '/projects/equipment' },
    { label: 'Calendar', path: '/projects/calendar' },
    { label: 'Meeting Mode', path: '/projects/meeting' },
  ],
  P11: [
    // Field Service: Tickets, scheduling, generators, contracts, reports
    { label: 'All Tickets', path: '/field-service' },
    { label: 'Calendar', path: '/field-service/schedule' },
    { label: 'Recurring', path: '/field-service/schedules' },
    { label: 'Generators', path: '/field-service/generators' },
    // Contracts is one page (the library); there is no Review Queue, Onboarding, Execution or Templates screen.
    // Those four links used to sit here and opened a contract page for a contract called "review" etc.
    { label: 'Contracts', path: '/field-service/contracts' },
    { label: 'Reports', path: '/field-service/reports' },
  ],
  P12: [
    // Technician: Pre-service briefings, AI job assistant, curation
    { label: 'My Jobs', path: '/technician' },
    { label: 'Schedule', path: '/technician/schedule' },
    { label: 'Briefings', path: '/technician/briefings' },
    { label: 'My Reports', path: '/technician/reports' },
    { label: 'Curation', path: '/technician/curation' },
  ],
  P13: [
    // Time Tracking: Team dashboard, timesheets, payroll, settings
    { label: 'Team Dashboard', path: '/time' },
    { label: 'Timesheets', path: '/time/timesheet-approval' },
    { label: 'Payroll Export', path: '/time/payroll-export' },
    { label: 'Time Off Requests', path: '/time/time-off-requests' },
    {
      label: 'Settings',
      path: '/time/settings',
      requireManagerOrAdmin: true,
      children: [
        { label: 'Pay Rules', path: '/time/settings' },
        { label: 'Holidays', path: '/time/settings/holidays' },
        { label: 'Flag Rules', path: '/time/settings/flags' },
      ],
    },
  ],
  P14: [
    // Recruiting: AI-powered talent acquisition
    { label: 'Dashboard', path: '/recruiting' },
    { label: 'Candidates', path: '/recruiting/candidates' },
    { label: 'Jobs', path: '/recruiting/jobs' },
    { label: 'Pipeline', path: '/recruiting/pipeline' },
  ],
  P15: [
    // Marketing: Content calendar, AI drafting, approvals, publishing
    { label: 'Dashboard', path: '/marketing' },
    { label: 'Calendar', path: '/marketing/calendar' },
    { label: 'Ideas', path: '/marketing/ideas' },
    { label: 'Composer', path: '/marketing/composer' },
    { label: 'Approvals', path: '/marketing/approvals' },
    { label: 'Media', path: '/marketing/media' },
    { label: 'Analytics', path: '/marketing/analytics' },
    { label: 'Settings', path: '/marketing/settings', requireManagerOrAdmin: true },
  ],
  P16: [
    // Huddle Hub: 6 v1 sections (Rocks deferred per 5/11)
    { label: 'Dashboard', path: '/huddle' },
    { label: 'Meetings', path: '/huddle/meetings' },
    { label: 'Action Items', path: '/huddle/action-items' },
    { label: 'Decisions', path: '/huddle/decisions' },
    { label: 'Announcements', path: '/huddle/announcements' },
    { label: 'Vision & Strategy', path: '/huddle/vto' },
    { label: 'Accountability', path: '/huddle/accountability' },
  ],
  P17: [],
  P18: [],
  P19: [], // Nav defined in modules/workshop/nav.ts via ModuleRegistry
  P20: [
    { label: 'Dashboard', path: '/insurance' },
    { label: 'Vendor Coverage', path: '/insurance/vendors' },
    { label: 'Customer Certificates', path: '/insurance/customers' },
    { label: 'Our Policies', path: '/insurance/policies' },
    { label: 'Claims', path: '/insurance/claims' },
    { label: "David's Activity", path: '/insurance/activity', requireManagerOrAdmin: true },
  ],
  AI_SYSTEMS: [], // Nav defined in modules/ai-systems/nav.ts via ModuleRegistry
  TRAINING: [],
  RUDY: [],
  PULSE: [],
  SHOP: [
    { label: 'Board', path: '/shop' },
  ],
};

// Cross-cutting navigation items available in all modules
export const SHARED_NAV_ITEMS: NavItem[] = [
  { label: 'Companies', path: '/companies' },
  { label: 'Contacts', path: '/contacts' },
];

interface ModuleContextValue {
  currentModule: ModuleId | null;
  setCurrentModule: (module: ModuleId) => void;
  modules: typeof MODULES;
  navItems: NavItem[];
  sharedNavItems: NavItem[];
  // Module access control
  userModules: ModuleId[];
  hasModuleAccess: (moduleId: ModuleId) => boolean;
  isOwner: boolean;
  accessibleModules: Module[];
}

const ModuleContext = createContext<ModuleContextValue | undefined>(undefined);

// Pages that are "module-neutral" — keep the previous module context active
const MODULE_NEUTRAL_PATHS = ['/profile', '/admin', '/organization', '/settings'];

// Map URL paths to modules for auto-detection
const PATH_TO_MODULE: Record<string, ModuleId | null> = {
  // Home page - no specific module
  '/': null,
  '/dashboard': null, // Legacy dashboard
  // P1: CRM (prospecting + pipeline + deals)
  '/prospecting': 'P1',
  '/pipeline': 'P1',
  '/crm': 'P1',
  // P2: Quoting
  '/quoting': 'P2',
  '/quotes': 'P2', // Legacy redirect
  '/customers': 'P2',
  '/equipment': 'P2',
  '/price-book': 'P2',
  '/service-parts': 'P2',
  '/inventory': 'P2',
  // P3: Contracts (merged into P11 Field Service)
  '/contracts': 'P11',
  // P4: Billing & Collections
  '/billing': 'P4',
  // P5: Customer Support
  '/support': 'P5',
  // P6: Central Command
  '/command': 'P6',
  // P7: Fleet Management
  '/assets': 'P7',
  // P8: Field Service Forms
  '/forms': 'P8',
  // P9: HR
  '/hr': 'P9',
  // P10: Project Management
  '/projects': 'P10',
  // P11: Field Service
  '/field-service': 'P11',
  // P12: Technician
  '/technician': 'P12',
  // P13: Time Tracking
  '/time': 'P13',
  // P14: Recruiting
  '/recruiting': 'P14',
  // P15: Marketing
  '/marketing': 'P15',
  // P16: Huddle Hub
  '/huddle': 'P16',
  // P17: Safety
  '/safety': 'P17',
  // P18: Investor Relations
  '/investors': 'P18',
  // P19: Workshop
  '/workshop': 'P19',
  '/shop': 'SHOP',
  // AI Systems
  '/ai-systems': 'AI_SYSTEMS',
};

function detectModuleFromPath(pathname: string): ModuleId | null {
  // Check for exact matches first
  if (pathname in PATH_TO_MODULE) {
    return PATH_TO_MODULE[pathname];
  }

  // Check for prefix matches
  for (const [path, module] of Object.entries(PATH_TO_MODULE)) {
    if (path !== '/' && path !== '/dashboard' && pathname.startsWith(path) && module !== null) {
      return module;
    }
  }

  // Return null for home/unknown routes (no module context)
  return null;
}

interface ModuleProviderProps {
  children: ReactNode;
}

export function ModuleProvider({ children }: ModuleProviderProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Initialize from URL or localStorage
  const [currentModule, setCurrentModuleState] = useState<ModuleId | null>(() => {
    const detected = detectModuleFromPath(location.pathname);
    return detected;
  });

  // Update module when URL changes — preserve current module on neutral pages (profile, admin, etc.)
  useEffect(() => {
    const isNeutral = MODULE_NEUTRAL_PATHS.some(
      (p) => location.pathname === p || location.pathname.startsWith(p + '/')
    );
    if (isNeutral) return; // Keep previous module active

    const detected = detectModuleFromPath(location.pathname);
    if (detected !== currentModule) {
      setCurrentModuleState(detected);
    }
  // Intentional: stable dependency omitted (`currentModule` would cause re-render loop)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Handle module switching
  const setCurrentModule = useCallback((module: ModuleId) => {
    const moduleConfig = MODULES[module];
    // External modules open in a new tab without changing the current module state
    if (moduleConfig.externalUrl) {
      window.open(moduleConfig.externalUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    setCurrentModuleState(module);
    navigate(moduleConfig.urlPrefix);
  }, [navigate]);

  // Check if current user is an organization owner
  const isOwner = useMemo(() => {
    if (!user?.email) return false;
    return OWNER_EMAILS.includes(user.email.toLowerCase());
  }, [user?.email]);

  // Canonical display order for all modules — single source of truth used by
  // both the owner shortcut path and the accessibleModules filter below.
  const ALL_MODULE_IDS: ModuleId[] = useMemo(() => [
    'P5', 'P1', 'P2', 'P11', 'P12', 'P13', 'P14', 'P15', 'P16', 'P17',
    'P18', 'P19', 'P20', 'P10', 'P9', 'P4', 'P7', 'P8', 'AI_SYSTEMS', 'P6', 'TRAINING', 'RUDY', 'PULSE', 'SHOP',
  ], []);

  // Get user's enabled modules
  const userModules = useMemo((): ModuleId[] => {
    if (!user) return [];
    // Owners have access to all modules
    if (isOwner) {
      return ALL_MODULE_IDS;
    }
    const modules = user.enabledModules || [];
    // Users with hrRole automatically get P9 access
    if (user.hrRole && !modules.includes('P9')) {
      return [...modules, 'P9'];
    }
    return modules;
  }, [user, isOwner, ALL_MODULE_IDS]);

  // Check if user has access to a specific module
  const hasModuleAccess = useCallback((moduleId: ModuleId): boolean => {
    // Owners always have access
    if (isOwner) return true;
    // Users with hrRole automatically get P9 access
    if (moduleId === 'P9' && user?.hrRole) return true;
    // Check user's enabled modules
    return userModules.includes(moduleId);
  }, [isOwner, userModules, user?.hrRole]);

  // Get list of accessible modules (P6 Data Explorer last in display order)
  const accessibleModules = useMemo((): Module[] => {
    return ALL_MODULE_IDS
      .filter(id => hasModuleAccess(id))
      .map(id => MODULES[id]);
  // Intentional: stable dependency omitted (`hasModuleAccess` is a local function)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userModules, isOwner]);

  const followUpCount = useFollowUpCount(currentModule === 'P2');
  const { data: mentionCount = 0 } = useQuoteMentionCount();
  // A user without the module has no tasks to count there; and gated agents' tasks are excluded (#5917).
  const agentTaskCount = useAgentTaskCount(
    currentModule ?? '',
    !!currentModule && hasModuleAccess(currentModule as ModuleId),
    blockedAgentConfigIds(hasModuleAccess),
  );
  // project_invoice_links is P10/P4-only in firestore.rules (#4189 slice 2); skip the count for anyone else.
  const unlinkedInvoiceCount = useUnlinkedInvoiceCount(currentModule === 'P10' && (hasModuleAccess('P10') || hasModuleAccess('P4')));
  const contentReviewCount = useContentReviewCount(currentModule === 'P15');
  const newMediaInquiryCount = useNewMediaInquiryCount(currentModule === 'P15');
  // The Quoting bar follows the quote worksheet switch (#6274 Q9); read only inside Quoting.
  const { worksheet: quoteWorksheet } = useQuoteWorksheetSwitch({ enabled: currentModule === 'P2' });

  // Get nav items from registry first, fallback to MODULE_NAV_ITEMS for non-migrated modules
  const navItems = useMemo(() => {
    if (!currentModule) return [];
    // Check registry first (for modules migrated to Module Isolation Architecture)
    const registryNavItems = ModuleRegistry.getNavItems(currentModule);
    let items = registryNavItems.length > 0
      ? registryNavItems
      : MODULE_NAV_ITEMS[currentModule] ?? [];
    if (currentModule === 'P2' && quoteWorksheet) items = worksheetNavItems;

    // Quoting, worksheet bar: follow-ups on the Follow-Up Queue row, unread @mentions on the All Quotes row. Both rows sit
    // inside the Quotes dropdown, and the nav shows a row's badge (the heading sums its rows), never the heading's own.
    if (currentModule === 'P2' && quoteWorksheet) {
      items = withBadgeAt(items, '/quoting/follow-up-queue', followUpCount);
      items = withBadgeAt(items, '/quoting/quotes', mentionCount);
    }

    // Quoting, today's bar (worksheet off): main's placement, unchanged (#8450 code M2).
    // Inject follow-up badge count for Quoting module
    if (currentModule === 'P2' && !quoteWorksheet && followUpCount > 0) {
      items = items.map((item) =>
        item.path === '/quoting/follow-up-queue'
          ? { ...item, badge: followUpCount }
          : item
      );
    }

    // Inject unread @mention badge count on the Quotes nav item
    if (currentModule === 'P2' && !quoteWorksheet && mentionCount > 0) {
      items = items.map((item) =>
        item.label === 'Quotes'
          ? { ...item, badge: mentionCount }
          : item
      );
    }

    // Inject agent task badge count on the module's agent nav item ("Agent Activity", or a label "Agents")
    items = withAgentTaskBadge(items, agentTaskCount);

    // Hide Invoice Matching nav when there are no unlinked invoices (P10 Projects)
    if (currentModule === 'P10' && unlinkedInvoiceCount === 0) {
      items = items.filter((item) => item.path !== '/projects/invoice-matching');
    }

    // Inject review-pending badge on the Content nav item (P15 Marketing)
    if (currentModule === 'P15' && contentReviewCount > 0) {
      items = items.map((item) =>
        item.path === '/marketing/content'
          ? { ...item, badge: contentReviewCount }
          : item
      );
    }

    // Inject new-media-inquiry badge on the PR nav item (P15 Marketing)
    if (currentModule === 'P15' && newMediaInquiryCount > 0) {
      items = items.map((item) =>
        item.path === '/marketing/pr'
          ? { ...item, badge: newMediaInquiryCount }
          : item
      );
    }

    return items;
  }, [currentModule, quoteWorksheet, followUpCount, mentionCount, agentTaskCount, unlinkedInvoiceCount, contentReviewCount, newMediaInquiryCount]);

  // ISS-544 diagnostic: tell bug reports which of "no user", "no current module" or "no nav items" was empty
  // when a page loses its tabs. Ids and counts only.
  const hasUser = !!user;
  const navItemCount = navItems.length;
  useEffect(() => {
    setNavState({ currentModule, navItemCount, hasUser });
  }, [currentModule, navItemCount, hasUser]);
  // When the provider goes away its last report must not outlive it.
  useEffect(() => clearNavState, []);

  const value = useMemo<ModuleContextValue>(() => ({
    currentModule,
    setCurrentModule,
    modules: MODULES,
    navItems,
    sharedNavItems: SHARED_NAV_ITEMS,
    // Module access control
    userModules,
    hasModuleAccess,
    isOwner,
    accessibleModules,
  }), [currentModule, setCurrentModule, navItems, userModules, hasModuleAccess, isOwner, accessibleModules]);

  return (
    <ModuleContext.Provider value={value}>
      {children}
    </ModuleContext.Provider>
  );
}

export function useModule() {
  const context = useContext(ModuleContext);
  if (!context) {
    throw new Error('useModule must be used within a ModuleProvider');
  }
  return context;
}

/**
 * `hasModuleAccess` when a ModuleProvider is mounted, or null when none is. For shared components
 * that also render where no provider exists (tests, the PWAs' stubs) and must keep working there;
 * `useModule` throws in that case. The caller decides what "no provider" means.
 */
export function useModuleAccessIfAvailable(): ModuleContextValue['hasModuleAccess'] | null {
  return useContext(ModuleContext)?.hasModuleAccess ?? null;
}
